import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight, ArrowDownUp, Wallet, Link2, Code2, Globe2, Building2, Store, Send, Layers, Landmark } from "lucide-react";
import Logo from "./Logo";

const productItems = [
  { label: "Payments", desc: "Accept and manage incoming payments", to: "/products#payments", icon: ArrowDownUp },
  { label: "Payouts", desc: "Pay customers, partners and suppliers", to: "/products#payouts", icon: Send },
  { label: "Multi-Currency Accounts", desc: "Hold and manage supported currencies", to: "/products#accounts", icon: Wallet },
  { label: "FX", desc: "Convert currencies with confidence", to: "/products#fx", icon: ArrowDownUp },
  { label: "Payment Links", desc: "Get paid with a link", to: "/products#payment-links", icon: Link2 },
  { label: "APIs", desc: "Build on Swiftwire infrastructure", to: "/products#apis", icon: Code2 },
];

const solutionItems = [
  { label: "Global Businesses", to: "/solutions#global-businesses", icon: Globe2 },
  { label: "Fintechs", to: "/solutions#fintechs", icon: Layers },
  { label: "Marketplaces", to: "/solutions#marketplaces", icon: Store },
  { label: "Remittance Companies", to: "/solutions#remittance", icon: Send },
  { label: "Platforms", to: "/solutions#platforms", icon: Building2 },
  { label: "Enterprises", to: "/solutions#enterprises", icon: Landmark },
];

const Dropdown = ({ label, items, open, setOpen, name }) => (
  <div className="relative" onMouseEnter={() => setOpen(name)} onMouseLeave={() => setOpen(null)}>
    <button
      data-testid={`nav-${name}-trigger`}
      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-[#334155] hover:text-[#1E3A6E] transition-colors"
      aria-expanded={open === name}
      aria-haspopup="true"
      onClick={() => setOpen(open === name ? null : name)}
    >
      {label}
      <ChevronDown size={15} strokeWidth={1.5} className={`transition-transform duration-200 ${open === name ? "rotate-180" : ""}`} />
    </button>
    {open === name && (
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50">
        <div className="w-[340px] rounded-2xl border border-[#E2E8F0] bg-white p-2 shadow-xl shadow-slate-900/5" data-testid={`nav-${name}-dropdown`}>
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={() => setOpen(null)}
              data-testid={`nav-${name}-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-[#F8FAFC] transition-colors"
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EFF5FD] text-[#1E3A6E]">
                <item.icon size={16} strokeWidth={1.5} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-[#0F172A]">{item.label}</span>
                {item.desc && <span className="block text-xs text-[#64748B] mt-0.5">{item.desc}</span>}
              </span>
            </Link>
          ))}
        </div>
      </div>
    )}
  </div>
);

const MobileSection = ({ label, items, onNavigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E2E8F0]">
      <button
        data-testid={`mobile-${label.toLowerCase()}-toggle`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-4 text-base font-semibold text-[#0F172A]"
        aria-expanded={open}
      >
        {label}
        <ChevronDown size={18} strokeWidth={1.5} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-4 pl-1">
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={onNavigate}
              className="block py-2 text-sm text-[#334155] hover:text-[#1E3A6E]"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-[#E2E8F0] bg-white/80 backdrop-blur-xl" data-testid="main-navbar">
      <nav className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <Link to="/" aria-label="Swiftwire Payments home">
          <Logo />
        </Link>

        <div className="hidden items-center lg:flex">
          <Dropdown label="Products" items={productItems} open={open} setOpen={setOpen} name="products" />
          <Dropdown label="Solutions" items={solutionItems} open={open} setOpen={setOpen} name="solutions" />
          <Link
            to="/developers"
            data-testid="nav-developers"
            className={`px-3 py-2 text-sm font-medium transition-colors ${isActive("/developers") ? "text-[#1E3A6E]" : "text-[#334155] hover:text-[#1E3A6E]"}`}
          >
            Developers
          </Link>
          <Link
            to="/company"
            data-testid="nav-company"
            className={`px-3 py-2 text-sm font-medium transition-colors ${isActive("/company") ? "text-[#1E3A6E]" : "text-[#334155] hover:text-[#1E3A6E]"}`}
          >
            Company
          </Link>
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/contact"
            data-testid="nav-sign-in"
            className="px-3 py-2 text-sm font-medium text-[#334155] hover:text-[#1E3A6E] transition-colors"
          >
            Sign In
          </Link>
          <Link
            to="/contact"
            data-testid="nav-get-started"
            className="group flex items-center gap-1.5 rounded-full bg-[#1E3A6E] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#16294e]"
          >
            Get Started
            <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-[#0F172A] lg:hidden"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={22} strokeWidth={1.5} />
        </button>
      </nav>
    </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-white lg:hidden" data-testid="mobile-menu">
          <div className="flex h-[68px] items-center justify-between border-b border-[#E2E8F0] px-4 sm:px-6">
            <Link to="/" onClick={() => setMobileOpen(false)}>
              <Logo />
            </Link>
            <button
              data-testid="mobile-menu-close"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[#0F172A]"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X size={22} strokeWidth={1.5} />
            </button>
          </div>
          <div className="h-[calc(100vh-68px)] overflow-y-auto px-4 py-4 sm:px-6">
            <MobileSection label="Products" items={productItems} onNavigate={() => setMobileOpen(false)} />
            <MobileSection label="Solutions" items={solutionItems} onNavigate={() => setMobileOpen(false)} />
            <Link
              to="/developers"
              onClick={() => setMobileOpen(false)}
              className="block border-b border-[#E2E8F0] py-4 text-base font-semibold text-[#0F172A]"
            >
              Developers
            </Link>
            <Link
              to="/company"
              onClick={() => setMobileOpen(false)}
              className="block border-b border-[#E2E8F0] py-4 text-base font-semibold text-[#0F172A]"
            >
              Company
            </Link>
            <div className="mt-6 flex flex-col gap-3 pb-10">
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-get-started"
                className="flex items-center justify-center gap-2 rounded-full bg-[#1E3A6E] px-5 py-3.5 text-sm font-semibold text-white"
              >
                Get Started <ArrowRight size={15} />
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center rounded-full border border-[#E2E8F0] bg-white px-5 py-3.5 text-sm font-semibold text-[#0F172A]"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
